// Applied Pay return / success page. Rendered on a cold redirect back from the hosted payment page
// (/umbrella/payment-complete?success=...&referenceId=...). The SPA has no in-memory state on this load;
// the confirmation heading + Reference / Amount / Fee come from the redirect query string, and the
// header + Contact/Coverage/Premium + receipt email are supplied from the sessionStorage snapshot captured
// before the redirect (no backend call).

import { toCurrency } from "src/infinity-ui";
import { getFooter } from "../templates/UMB.new-application-form";

export interface AppliedPayReturnParams {
    success: boolean;
    amountMinor: string;
    feeMinor: string;
    referenceId: string;
    pspReferenceId: string;
}

export interface AppliedPayReturnExtras {
    email?: string;
    summary?: any;
    header?: string;
}

export function parseAppliedPayReturnParams(search?: string): AppliedPayReturnParams {
    const params = new URLSearchParams(search ?? location.search);
    const get = (key: string) => params.get(key) || "";

    return {
        success: get("success").toLowerCase() === "true",
        amountMinor: get("amount"),
        feeMinor: get("fee"),
        referenceId: get("referenceId"),
        pspReferenceId: get("pspReferenceId").trim()
    };
}

export function arcPaymentComplete(params: AppliedPayReturnParams, extras?: AppliedPayReturnExtras, receiptNumberEnabled = false): any {
    const heading = params.success
        ? "<h1>Thank you for your payment.</h1>"
        : "<h1>Thank you for your submission.</h1>";

    // Session snapshot present when we have any of the header / email / summary. With no session, the page
    // drops the header, receipt email, summary and footer chrome — the thank-you heading and any URL-sourced
    // details (Reference / Amount / Fee) still render.
    const hasSession = !!(extras?.header || extras?.email || extras?.summary);

    const items: any[] = [];

    if (extras?.header) {
        items.push({ type: "none", className: "global-header arc-content", label: extras.header });
    }

    items.push({ type: "none", itemId: "appliedPayThankYou", className: "container c-0-65-92 arc-content", label: heading });

    // Reference / Amount / Fee come from the redirect query string (always available on return). The Reference
    // Number is Applied Pay's receipt number (pspReferenceId) when the receipt-number flag is on and the redirect
    // carries one; otherwise it is the referenceId.
    const fieldClass = "w-100 pad-x-0";
    const details: any[] = [];
    const amount = Number(params.amountMinor);
    const fee = Number(params.feeMinor);
    const referenceNumber = receiptNumberEnabled && params.pspReferenceId ? params.pspReferenceId : params.referenceId;
    if (referenceNumber) {
        details.push({ type: "text", itemId: "appliedPayReference", label: "Reference Number:", className: fieldClass, disabled: true, value: referenceNumber });
    }
    if (params.amountMinor && isFinite(amount)) {
        details.push({ type: "text", itemId: "appliedPayAmount", label: "Payment Amount:", className: fieldClass, disabled: true, value: toCurrency(amount / 100) });
    }
    if (params.feeMinor && isFinite(fee)) {
        details.push({ type: "text", itemId: "appliedPayFee", label: "Convenience Fee:", className: fieldClass, disabled: true, value: toCurrency(fee / 100) });
    }
    if (details.length) {
        items.push({ type: "none", itemId: "appliedPayDetails", className: "container arc-content", items: details });
    }

    if (extras?.email) {
        items.push({
            type: "none",
            itemId: "appliedPayReceiptConfirmation",
            className: "container arc-content",
            items: [
                { type: "none", label: "A record of this transaction has been sent to:", className: "col-md-6" },
                { type: "text", itemId: "appliedPayReceiptEmail", className: "col-md-6 pad-x-0", disabled: true, value: extras.email }
            ]
        });
    }

    if (extras?.summary) {
        items.push(extras.summary);
    }

    if (hasSession) {
        items.push({ type: "none", className: "global-footer arc-content", label: getFooter() });
    }

    return {
        type: "none",
        itemId: "paymentComplete",
        visible: true,
        className: "main umbrella",
        items
    };
}
